import * as THREE from 'three';
import type { TerrainMeta, TileSystem, TileUserData, MercTile } from './types';
import { TILE_EXT } from './terrain';

const TILE_COLS = 8;
const TILE_ROWS = 8;

// ── Placeholder texture generation ───────────────────────────────────────────

function makeTileTex(size: number, tx: number, tz: number, lod: 0 | 1): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    const hue = ((tx * TILE_ROWS + tz) / (TILE_COLS * TILE_ROWS)) * 360;
    const sat = 70;
    const lit = lod === 0 ? 38 : 62;

    ctx.fillStyle = `hsl(${hue.toFixed(0)}, ${sat}%, ${lit}%)`;
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = lod === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.4)';
    ctx.lineWidth   = Math.max(1, size / 64);
    ctx.strokeRect(1, 1, size - 2, size - 2);

    const fontSize = Math.max(10, size / 8);
    ctx.font         = `bold ${fontSize}px monospace`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle    = 'rgba(255,255,255,0.9)';
    ctx.fillText(`${tx},${tz}`, size / 2, size * 0.38);
    ctx.font      = `${Math.round(fontSize * 0.7)}px monospace`;
    ctx.fillStyle = lod === 0 ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.6)';
    ctx.fillText(`LOD${lod}`, size / 2, size * 0.62);

    return new THREE.CanvasTexture(canvas);
}

// ── Web Mercator helpers ──────────────────────────────────────────────────────

// Subtle colour grading for satellite/orthophoto tiles — the raw imagery
// tends to look flat/washed-out, so lift saturation and contrast a touch and
// add a faint warm tint. Kept gentle so it doesn't fight the LOD debug tint.
function applyColorGrading(mat: THREE.MeshStandardMaterial): void {
    mat.onBeforeCompile = (shader) => {
        shader.fragmentShader = shader.fragmentShader.replace(
            '#include <map_fragment>',
            `#include <map_fragment>
            {
                float _lum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
                diffuseColor.rgb = mix(vec3(_lum), diffuseColor.rgb, 1.18);
                diffuseColor.rgb = (diffuseColor.rgb - 0.5) * 1.18 + 0.5;
                diffuseColor.rgb += vec3(0.015, 0.008, -0.01);
                diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
            }`,
        );
    };
}

function lonLatToMercTile(lon: number, lat: number, zoom: number): MercTile {
    const n      = Math.pow(2, zoom);
    const x      = Math.floor((lon + 180) / 360 * n);
    const latRad = lat * Math.PI / 180;
    const y      = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
    return { x, y, z: zoom };
}

// ── Build tile sub-geometries ─────────────────────────────────────────────────

export function buildTileSystem(
    rawPositions: Float32Array,
    rawUVs: Float32Array,
    segX: number,
    segZ: number,
    meta: TerrainMeta,
    maskTex: THREE.Texture,
    slug: string,
): TileSystem {
    const { world, bounds } = meta;
    const W = world.width;
    const H = world.height;
    const satelliteVersion = meta.satelliteVersion;

    const tileSegX = Math.floor(segX / TILE_COLS);
    const tileSegZ = Math.floor(segZ / TILE_ROWS);

    const tiles: THREE.Mesh[] = [];

    for (let tz = 0; tz < TILE_ROWS; tz++) {
        for (let tx = 0; tx < TILE_COLS; tx++) {
            const ix0 = tx * tileSegX;
            const ix1 = tx === TILE_COLS - 1 ? segX : (tx + 1) * tileSegX;
            const iz0 = tz * tileSegZ;
            const iz1 = tz === TILE_ROWS - 1 ? segZ : (tz + 1) * tileSegZ;

            const nCols  = ix1 - ix0 + 1;
            const nRows  = iz1 - iz0 + 1;
            const vCount = nCols * nRows;

            const tilePos = new Float32Array(vCount * 3);
            const tileUVs = new Float32Array(vCount * 2);

            const uMin = ix0 / segX;
            const uMax = ix1 / segX;
            const vMin = (TILE_ROWS - 1 - tz) / TILE_ROWS;
            const vMax = (TILE_ROWS - tz) / TILE_ROWS;

            let vi = 0;
            for (let iz = iz0; iz <= iz1; iz++) {
                for (let ix = ix0; ix <= ix1; ix++) {
                    const src = iz * (segX + 1) + ix;
                    tilePos[vi * 3 + 0] = rawPositions[src * 3 + 0];
                    tilePos[vi * 3 + 1] = rawPositions[src * 3 + 1];
                    tilePos[vi * 3 + 2] = rawPositions[src * 3 + 2];

                    const uOrig = rawUVs[src * 2 + 0];
                    const vOrig = rawUVs[src * 2 + 1];
                    tileUVs[vi * 2 + 0] = (uOrig - uMin) / (uMax - uMin);
                    tileUVs[vi * 2 + 1] = (vOrig - vMin) / (vMax - vMin);
                    vi++;
                }
            }

            const segCols = nCols - 1;
            const segRows = nRows - 1;
            const tileIdx = new Uint32Array(segCols * segRows * 6);
            let ii = 0;
            for (let r = 0; r < segRows; r++) {
                for (let c = 0; c < segCols; c++) {
                    const a  = r * nCols + c;
                    const b  = (r + 1) * nCols + c;
                    const c0 = (r + 1) * nCols + (c + 1);
                    const d  = r * nCols + (c + 1);
                    tileIdx[ii++] = a;
                    tileIdx[ii++] = b;
                    tileIdx[ii++] = d;
                    tileIdx[ii++] = b;
                    tileIdx[ii++] = c0;
                    tileIdx[ii++] = d;
                }
            }

            const geo = new THREE.BufferGeometry();
            geo.setAttribute('position', new THREE.BufferAttribute(tilePos, 3));
            geo.setAttribute('uv',       new THREE.BufferAttribute(tileUVs, 2));
            geo.setIndex(new THREE.BufferAttribute(tileIdx, 1));
            geo.computeVertexNormals();

            const tileAlpha = maskTex.clone() as THREE.Texture;
            tileAlpha.repeat.set(1 / TILE_COLS, 1 / TILE_ROWS);
            tileAlpha.offset.set(tx / TILE_COLS, (TILE_ROWS - 1 - tz) / TILE_ROWS);
            tileAlpha.needsUpdate = true;

            const tex0 = makeTileTex(256, tx, tz, 0);
            const tex1 = makeTileTex(128, tx, tz, 1);

            const matBase = {
                alphaMap:    tileAlpha,
                alphaTest:   0.5,
                transparent: true,
                roughness:   0.85,
                metalness:   0.0,
                side: THREE.DoubleSide,
            };
            const mat0 = new THREE.MeshStandardMaterial({ ...matBase, map: tex0 });
            const mat1 = new THREE.MeshStandardMaterial({ ...matBase, map: tex1 });
            applyColorGrading(mat0);
            applyColorGrading(mat1);

            const mesh = new THREE.Mesh(geo, mat1);
            mesh.receiveShadow = true;
            mesh.name = `tile-${tx}-${tz}`;

            const cx = ((ix0 + ix1) / 2 / segX - 0.5) * W;
            const cz = ((iz0 + iz1) / 2 / segZ - 0.5) * H;
            const center = new THREE.Vector3(cx, 0, cz);

            const lonCenter = bounds.minLon + (ix0 + ix1) / 2 / segX * (bounds.maxLon - bounds.minLon);
            const latCenter = bounds.maxLat - (iz0 + iz1) / 2 / segZ * (bounds.maxLat - bounds.minLat);
            const mercTile  = lonLatToMercTile(lonCenter, latCenter, 12);

            const userData: TileUserData = {
                lod: 1, tx, tz, center, mat0, mat1, tex0, tex1, tileAlpha, mercTile,
                lod0Loaded: false, lod0Requested: false, loadLod0: () => {},
            };
            mesh.userData = userData;
            tiles.push(mesh);
        }
    }

    const group = new THREE.Group();
    for (const tile of tiles) group.add(tile);

    const tileSystem: TileSystem = { tiles, group, disposed: false };

    if (satelliteVersion) {
        loadRealTiles(tileSystem, slug, satelliteVersion);
    }

    return tileSystem;
}

// ── Real orthophoto texture loading ────────────────────────────────────────
//
// LOD1 (far, small) is cheap and loaded eagerly for every tile so the region
// always has a real overview. LOD0 (near, large — ~1.5MB/tile) is loaded
// lazily, only once a tile actually needs to switch to near LOD (see
// updateTileLOD), to avoid an ~100MB eager fetch per region switch.

function loadTileTexture(
    loader: THREE.TextureLoader,
    url: string,
    tileSystem: TileSystem,
    ud: TileUserData,
    slot: 0 | 1,
    fallbackUrl?: string,
): void {
    const mat = slot === 0 ? ud.mat0 : ud.mat1;
    loader.load(
        url,
        (loadedTex) => {
            if (tileSystem.disposed) { loadedTex.dispose(); return; }
            const oldTex = slot === 0 ? ud.tex0 : ud.tex1;
            oldTex.dispose();
            if (slot === 0) { ud.tex0 = loadedTex; ud.lod0Loaded = true; } else { ud.tex1 = loadedTex; }
            mat.map = loadedTex;
            mat.needsUpdate = true;
        },
        undefined,
        () => {
            if (fallbackUrl) {
                loadTileTexture(loader, fallbackUrl, tileSystem, ud, slot);
            }
            // else: missing tile image — keep placeholder
        },
    );
}

function loadRealTiles(tileSystem: TileSystem, slug: string, satelliteVersion: string): void {
    const loader = new THREE.TextureLoader();
    const v = `?v=${satelliteVersion}`;
    const fallbackExt = TILE_EXT === 'webp' ? 'jpg' : undefined;

    for (const tile of tileSystem.tiles) {
        const ud = tile.userData as TileUserData;
        const base = `/data/${slug}/satellite/${ud.tx}_${ud.tz}`;

        loadTileTexture(
            loader, `${base}_lod1.${TILE_EXT}${v}`, tileSystem, ud, 1,
            fallbackExt ? `${base}_lod1.${fallbackExt}${v}` : undefined,
        );

        ud.loadLod0 = () => {
            if (ud.lod0Requested) return;
            ud.lod0Requested = true;
            loadTileTexture(
                loader, `${base}_lod0.${TILE_EXT}${v}`, tileSystem, ud, 0,
                fallbackExt ? `${base}_lod0.${fallbackExt}${v}` : undefined,
            );
        };
    }
}

// ── Scene management ──────────────────────────────────────────────────────────

export function addTileSystemToScene(tileSystem: TileSystem, parent: THREE.Object3D): void {
    parent.add(tileSystem.group);
}

export function removeTileSystemFromScene(tileSystem: TileSystem, parent: THREE.Object3D): void {
    parent.remove(tileSystem.group);
}

// ── LOD debug overlay ────────────────────────────────────────────────────────
// Tints tiles by their currently active LOD (green = near/LOD0, orange =
// far/LOD1) so an admin can see exactly where the LOD switch boundary falls
// while tuning the LOD distance slider.

const DEBUG_COLOR_LOD0 = 0x66ff88;
const DEBUG_COLOR_LOD1 = 0xff9955;
const DEBUG_COLOR_OFF  = 0xffffff;

export function setLodDebugOverlay(tileSystem: TileSystem, enabled: boolean): void {
    for (const tile of tileSystem.tiles) {
        const ud = tile.userData as TileUserData;
        ud.mat0.color.set(enabled ? DEBUG_COLOR_LOD0 : DEBUG_COLOR_OFF);
        ud.mat1.color.set(enabled ? DEBUG_COLOR_LOD1 : DEBUG_COLOR_OFF);
    }
}

// ── Per-frame LOD update ──────────────────────────────────────────────────────

export interface LodCounts {
    lod0: number;
    lod1: number;
}

export function updateTileLOD(
    tileSystem: TileSystem,
    camera: THREE.Camera,
    lodDistance: number,
    worldRoot?: THREE.Object3D,
): LodCounts {
    // Use local-space camera position when worldRoot is scaled (AR mode)
    const camLocal = worldRoot
        ? worldRoot.worldToLocal(camera.position.clone())
        : camera.position;

    let lod0 = 0;
    let lod1 = 0;

    for (const tile of tileSystem.tiles) {
        const ud   = tile.userData as TileUserData;
        const dist = camLocal.distanceTo(ud.center);
        const want = dist < lodDistance ? 0 : 1;

        if (want === 0) {
            ud.loadLod0(); // no-op if already requested
            // Only actually switch once the real texture has arrived — keep
            // showing the (already-real) LOD1 photo instead of flashing the
            // LOD0 placeholder while it loads.
            if (ud.lod0Loaded && ud.lod !== 0) {
                tile.material = ud.mat0;
                ud.lod = 0;
            }
        } else if (want === 1 && ud.lod !== 1 && !ud.lod0Loaded) {
            // Only downgrade while LOD0 is still a placeholder — once the real
            // high-res texture has loaded, stay on it (already paid for; no
            // benefit to switching back).
            tile.material = ud.mat1;
            ud.lod = 1;
        }

        if (ud.lod === 0) lod0++; else lod1++;
    }

    return { lod0, lod1 };
}

// ── Cleanup ───────────────────────────────────────────────────────────────────

export function disposeTileSystem(tileSystem: TileSystem): void {
    tileSystem.disposed = true;
    for (const tile of tileSystem.tiles) {
        const ud = tile.userData as TileUserData;
        tile.geometry.dispose();
        ud.mat0.dispose();
        ud.mat1.dispose();
        ud.tex0.dispose();
        ud.tex1.dispose();
        ud.tileAlpha.dispose();
    }
}
